import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, ScaleControl, ImageOverlay, Marker, Tooltip, GeoJSON } from "react-leaflet";
import type { LatLngBoundsExpression, Layer, LeafletMouseEvent, StyleFunction, GeoJSON as LeafletGeoJSON } from "leaflet";
import type { Feature, Geometry, FeatureCollection } from "geojson";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import TeamHeader from "../TeamHeader/TeamHeader";
import "./Environmental.css";
import countiesGeoJson from '../GisTab/counties_southeast.json';

import tempPic from './assets/temperature-card.png'
import humidityPic from './assets/humidity-card.png'
import windPic from './assets/wind-speed-card.png'

type VariableKey = "temp" | "humid" | "wspd";

interface VariableConfig {
    key: VariableKey;
    label: string;
    shortDescription: string;
    aboutTitle: string;
    aboutDescription: string;
    image: string;
}

const LEGEND_BAR_HEIGHT = 180; // px — must match .env-legend-row height in CSS
const MIN_LABEL_GAP = 14;      // px — minimum vertical space between label centers

// Given top-position percentages (any order, 0 = top of bar, 100 = bottom),
// push labels apart just enough to avoid overlapping text, then return the
// adjusted positions in the SAME order as the input array.
const declutterTopPositions = (topPercents: number[]): number[] => {
    const raw = topPercents.map(p => (p / 100) * LEGEND_BAR_HEIGHT);

    // Work on a copy sorted by actual position, remembering original indices
    const order = raw.map((_, i) => i).sort((a, b) => raw[a] - raw[b]);
    const sortedPx = order.map(i => raw[i]);

    for (let i = 1; i < sortedPx.length; i++) {
        if (sortedPx[i] - sortedPx[i - 1] < MIN_LABEL_GAP) {
            sortedPx[i] = sortedPx[i - 1] + MIN_LABEL_GAP;
        }
    }
    const overflow = sortedPx[sortedPx.length - 1] - LEGEND_BAR_HEIGHT;
    if (overflow > 0) {
        for (let i = 0; i < sortedPx.length; i++) sortedPx[i] -= overflow;
        for (let i = 1; i < sortedPx.length; i++) {
            if (sortedPx[i] - sortedPx[i - 1] < MIN_LABEL_GAP) {
                sortedPx[i] = sortedPx[i - 1] + MIN_LABEL_GAP;
            }
        }
    }
    const clamped = sortedPx.map(v => Math.max(0, Math.min(LEGEND_BAR_HEIGHT, v)));

    // map back to the caller's original order
    const result = new Array(topPercents.length);
    order.forEach((originalIdx, sortedIdx) => {
        result[originalIdx] = clamped[sortedIdx];
    });
    return result;
};

// currentData.timestamp is a naive "YYYY-MM-DD HH:mm:ss" string that the backend has
// already converted from UTC into America/New_York wall-clock time. We parse it
// manually (rather than `new Date(rawString)`) because browsers are inconsistent
// about whether a timezone-less string like this is treated as local time or UTC.
const formatEasternTimestamp = (raw: string): string => {
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
    if (!match) return `${raw} ET`;

    const [, year, month, day, hour, minute] = match;
    // Constructing with explicit numeric args treats them as local components —
    // we're just using Date as a formatting helper here, not converting timezones.
    const dt = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));

    const datePart = dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const timePart = dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

    return `${datePart}, ${timePart} ET`;
};

const VARIABLES: VariableConfig[] = [
    {
        key: "temp",
        label: "24-hour Temperature Variation",
        shortDescription: "Current air temperature across Florida",
        aboutTitle: "About the Air Temperature Map",
        aboutDescription:
            "The Air Temperature map plots the air temperature (°F) at the standard height of 1.5 m (5 feet) across Florida. Warmer areas are shaded in red and orange tones, while cooler areas appear in blue. Use the hour slider to step through the day and see how temperatures shift from morning through evening.",
        image: tempPic,
    },
    {
        key: "humid",
        label: "24-hour Relative Humidity Variation",
        shortDescription: "Relative humidity levels statewide",
        aboutTitle: "About the Humidity Map",
        aboutDescription:
            "The Humidity map plots relative humidity (%) across Florida, showing how much moisture is in the air relative to what it can hold at the current temperature. Darker blue areas indicate higher humidity, which is common in Florida due to its coastal, subtropical climate. This is useful for tracking muggy conditions, fog potential, and comfort levels throughout the day.",
        image: humidityPic,
    },
    {
        key: "wspd",
        label: "24-hour Wind Speed and Direction",
        shortDescription: "Surface wind speed and direction",
        aboutTitle: "About the Wind Speed Map",
        aboutDescription:
            "The Wind Speed map plots surface wind speed (mph) across Florida. Brighter yellow-green areas indicate stronger winds, while darker purple areas indicate calmer conditions. Arrows show wind direction, pointing the way the wind is blowing. This map is helpful for tracking sea breeze fronts, approaching weather systems, and general wind patterns across the state throughout the day.",
        image: windPic,
    },
];

interface LegendBin {
    from: number;
    to: number;
    color: string;
}

interface WindVector {
    lat: number;
    lon: number;
    direction: number; // degrees, meteorological "from" convention
    speed: number | null;
}

interface VariableDataResponse {
    variable: string;
    time_index: number;
    timestamp: string;
    unit: string;
    min: number | null;
    max: number | null;
    legend_levels: number[];
    legend_bins: LegendBin[];
    bounds: LatLngBoundsExpression;
    image: string;
}

interface WindDataResponse {
    time_index: number;
    timestamp: string;
    unit: string;
    vectors: WindVector[];
}

const DEFAULT_CENTER: [number, number] = [27.8, -82.5];

// Rotated arrow icon for wind direction. `direction` is meteorological
// degrees (where the wind is coming FROM). We rotate by direction + 180
// so the arrow points the way the wind is blowing TO. If your arrows look
// backwards on the map, drop the "+ 180".
const windArrowIcon = (direction: number) => {
    const rotation = (direction + 180) % 360;
    return L.divIcon({
        className: "wind-arrow-icon",
        html: `<svg width="16" height="16" viewBox="0 0 24 24" style="transform: rotate(${rotation}deg);">
                 <path d="M12 3 L12 21 M12 3 L6 10 M12 3 L18 10" stroke="#1f2937" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
               </svg>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });
};

interface CountyFeatureProps {
    NAME: string;
    STATEFP: string;
    STATE?: string;
    [key: string]: any;
}

const CountyBordersLayer: React.FC = () => {
    const geoJsonRef = useRef<LeafletGeoJSON | null>(null);
    const [geoData, setGeoData] = useState<FeatureCollection | null>(null);

    useEffect(() => {
        setGeoData(countiesGeoJson as FeatureCollection);
    }, []);

    // Florida only — filter out AL, GA, SC, TX counties from the shared file
    const floridaGeoData = useMemo<FeatureCollection | null>(() => {
        if (!geoData) return null;
        return {
            ...geoData,
            features: geoData.features.filter((feature) => {
                const props = feature.properties as CountyFeatureProps;
                const state = props?.STATE ?? "";
                // STATEFP "12" is the FIPS code for Florida; also check STATE name as a fallback
                return props?.STATEFP === "12" || state === "Florida" || state === "FL";
            }),
        };
    }, [geoData]);

    // No fill — just an outline
    const styleFn: StyleFunction<CountyFeatureProps> = useCallback(() => ({
        fillOpacity: 0,
        color: "#374151",
        weight: 1,
        interactive: true,
    }), []);

    const onEachFeature = useCallback((feature: Feature<Geometry, CountyFeatureProps>, layer: Layer) => {
        const name = feature.properties?.NAME ?? "";

        layer.bindTooltip(
            `${name} County`,
            {
                className: "county-tooltip",
                sticky: false,
                direction: "top",
            }
        );

        layer.on({
            mouseover: (e: LeafletMouseEvent) => {
                const target = e.target;
                target.setStyle({ weight: 2.5, color: "#111827" });
                target.bringToFront();
                target.openTooltip();
            },
            mouseout: (e: LeafletMouseEvent) => {
                const target = e.target;
                geoJsonRef.current?.resetStyle(target);
                target.closeTooltip();
            },
        });
    }, []);

    if (!floridaGeoData) return null;

    return (
        <GeoJSON
            ref={geoJsonRef}
            data={floridaGeoData}
            style={styleFn as any}
            onEachFeature={onEachFeature as any}
        />
    );
};

const Environmental = () => {
    const [loadingMeta, setLoadingMeta] = useState(true);
    const [timeSteps, setTimeSteps] = useState(24);
    const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);

    const [activeTab, setActiveTab] = useState<VariableKey | null>(null);
    const [timeIndex, setTimeIndex] = useState(0);

    const [loadingData, setLoadingData] = useState(false);
    const [currentData, setCurrentData] = useState<VariableDataResponse | null>(null);
    const [windData, setWindData] = useState<WindDataResponse | null>(null);

    const [showWindArrows, setShowWindArrows] = useState(true);
    const [showCounties, setShowCounties] = useState(true);

    const cache = useRef<Map<string, VariableDataResponse>>(new Map());
    const windCache = useRef<Map<number, WindDataResponse>>(new Map());

    useEffect(() => {
        const loadMeta = async () => {
            try {
                const response = await fetch("/api/environmental/", {
                    credentials: "same-origin",
                });
                const data = await response.json();

                setTimeSteps(data.dimensions.time ?? 24);

                const lat = data.coordinates.find((c: any) => c.name === "lat");
                const lon = data.coordinates.find((c: any) => c.name === "lon");
                if (lat?.min != null && lat?.max != null && lon?.min != null && lon?.max != null) {
                    setMapCenter([(lat.min + lat.max) / 2, (lon.min + lon.max) / 2]);
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingMeta(false);
            }
        };

        loadMeta();
    }, []);

    const loadVariableData = useCallback(async (variable: VariableKey, index: number) => {
        const cacheKey = `${variable}-${index}`;
        const cached = cache.current.get(cacheKey);
        if (cached) {
            setCurrentData(cached);
            return;
        }

        setLoadingData(true);
        try {
            const response = await fetch(
                `/api/environmental/data/?variable=${variable}&time_index=${index}`,
                { credentials: "same-origin" }
            );
            const data: VariableDataResponse = await response.json();
            cache.current.set(cacheKey, data);
            setCurrentData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingData(false);
        }
    }, []);

    const loadWindData = useCallback(async (index: number) => {
        const cached = windCache.current.get(index);
        if (cached) {
            setWindData(cached);
            return;
        }
        try {
            const response = await fetch(
                `/api/environmental/wind/?time_index=${index}&stride=4`,
                { credentials: "same-origin" }
            );
            const data: WindDataResponse = await response.json();
            windCache.current.set(index, data);
            setWindData(data);
        } catch (err) {
            console.error(err);
        }
    }, []);

    useEffect(() => {
        if (activeTab) {
            loadVariableData(activeTab, timeIndex);
        }
        if (activeTab === "wspd") {
            loadWindData(timeIndex);
        } else {
            setWindData(null);
        }
    }, [activeTab, timeIndex, loadVariableData, loadWindData]);

    const handleSelectVariable = (key: VariableKey) => {
        setCurrentData(null);
        setWindData(null);
        setTimeIndex(0);
        setActiveTab(key);
    };

    const handleBackToMenu = () => {
        setActiveTab(null);
        setCurrentData(null);
        setWindData(null);
    };

    const activeConfig = VARIABLES.find(v => v.key === activeTab) ?? null;

    // Position (0-100%) of a legend level along the min→max gradient bar
    const legendPct = (v: number) => {
        if (!currentData || currentData.min == null || currentData.max == null) return 0;
        const { min, max } = currentData;
        if (max === min) return 0;
        return Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100));
    };

    return (
        <div className="customer-directory-body">
            <TeamHeader title="Environmental" />

            <div className="support-container">
                {activeConfig ? (
                    <div className="env-detail-header">
                        <button type="button" className="env-back-button" onClick={handleBackToMenu}>
                            &larr; Back to maps
                        </button>
                        <div>
                            <h1 className="support-page-title">{activeConfig.label}</h1>
                            <p className="support-page-sub">{activeConfig.shortDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="support-page-header">
                        <div>
                            <h1 className="support-page-title">Environmental Dataset</h1>
                            <p className="support-page-sub">
                                NetCDF metadata loaded from the Django backend.
                            </p>
                        </div>
                    </div>
                )}

                {loadingMeta ? (
                    <div className="tv-loading">Loading dataset...</div>
                ) : activeConfig ? (
                    <div className="env-map-section">
                        <div className="env-controls-row">
                            <div className="env-time-control">
                                <label htmlFor="time-slider">
                                    Hour {timeIndex}
                                    {currentData?.timestamp ? ` — ${formatEasternTimestamp(currentData.timestamp)}` : ""}
                                </label>
                                <input
                                    id="time-slider"
                                    type="range"
                                    min={0}
                                    max={timeSteps - 1}
                                    value={timeIndex}
                                    onChange={e => setTimeIndex(Number(e.target.value))}
                                />
                            </div>

                            <div className="env-layer-toggles">
                                {activeTab === "wspd" && (
                                    <label className="env-toggle">
                                        <span className="env-toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={showWindArrows}
                                                onChange={e => setShowWindArrows(e.target.checked)}
                                            />
                                            <span className="env-toggle-thumb" />
                                        </span>
                                        Wind arrows
                                    </label>
                                )}

                                <label className="env-toggle">
                                    <span className="env-toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={showCounties}
                                            onChange={e => setShowCounties(e.target.checked)}
                                        />
                                        <span className="env-toggle-thumb" />
                                    </span>
                                    Counties
                                </label>
                            </div>
                        </div>

                        <div className="env-map-wrapper">
                            {loadingData && <div className="env-map-loading">Loading layer...</div>}

                            <MapContainer
                                center={mapCenter}
                                zoom={7}
                                scrollWheelZoom={true}
                                className="env-map"
                            >
                                <TileLayer
                                    attribution='&copy; OpenStreetMap contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <ScaleControl position="bottomleft" />

                                {showCounties && <CountyBordersLayer />}

                                {currentData && (
                                    <ImageOverlay
                                        key={`${currentData.variable}-${currentData.time_index}`}
                                        url={currentData.image}
                                        bounds={currentData.bounds}
                                        opacity={0.75}
                                    />
                                )}

                                {showWindArrows && activeTab === "wspd" && windData?.vectors?.map((v, idx) => (
                                    <Marker
                                        key={`wind-${idx}`}
                                        position={[v.lat, v.lon]}
                                        icon={windArrowIcon(v.direction)}
                                    >
                                        <Tooltip direction="top" offset={[0, -6]}>
                                            {v.direction}° {v.speed != null ? `· ${v.speed} mph` : ""}
                                        </Tooltip>
                                    </Marker>
                                ))}
                            </MapContainer>

                            {currentData && (() => {
                                const bins = currentData.legend_bins ?? [];
                                const boundaries = bins.length
                                    ? [bins[0].from, ...bins.map(b => b.to)]
                                    : [];
                                // top-based %, ascending as value descends (highest first)
                                const truePct = boundaries.map(v => 100 - legendPct(v));
                                const labelPx = declutterTopPositions(truePct);

                                return (
                                    <div className="env-legend">
                                        <div className="env-legend-unit">{currentData.unit}</div>
                                        <div className="env-legend-row">
                                            <div className="env-legend-bar">
                                                {bins.map((bin, idx) => (
                                                    <div
                                                        key={idx}
                                                        className="env-legend-bin"
                                                        style={{
                                                            top: `${100 - legendPct(bin.to)}%`,
                                                            height: `${legendPct(bin.to) - legendPct(bin.from)}%`,
                                                            backgroundColor: bin.color,
                                                        }}
                                                    />
                                                ))}
                                            </div>
                                            <div className="env-legend-labels">
                                                {boundaries.map((v, idx) => {
                                                    const truePx = (truePct[idx] / 100) * LEGEND_BAR_HEIGHT;
                                                    const labelYPx = labelPx[idx];
                                                    const needsLeader = Math.abs(labelYPx - truePx) > 2;
                                                    return (
                                                        <React.Fragment key={idx}>
                                                            {needsLeader && (
                                                                <span
                                                                    className="env-legend-leader"
                                                                    style={{ top: `${truePx}px` }}
                                                                />
                                                            )}
                                                            <span
                                                                className="env-legend-label"
                                                                style={{ top: `${labelYPx}px` }}
                                                            >
                                                                {v}
                                                            </span>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="env-about">
                            <h2 className="env-about-title">{activeConfig.aboutTitle}</h2>
                            <p className="env-about-desc">{activeConfig.aboutDescription}</p>
                        </div>
                    </div>
                ) : (
                    <div className="env-card-grid">
                        {VARIABLES.map(variable => (
                            <button
                                key={variable.key}
                                type="button"
                                className="env-card"
                                onClick={() => handleSelectVariable(variable.key)}
                            >
                                <div className="env-card-image-wrapper">
                                    <img src={variable.image} alt={variable.label} className="env-card-image" />
                                </div>
                                <div className="env-card-body">
                                    <h3 className="env-card-title">{variable.label}</h3>
                                    <p className="env-card-desc">{variable.shortDescription}</p>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Environmental;